import type { Feature, FeatureCollection } from 'geojson';

import { API_BASE_URL, motogoFetch } from './api';

/**
 * Client for the backend's route-planning endpoints (`POST /route`, `GET /geocode`).
 * Mode/gravel semantics live server-side (GraphHopper custom models); this module just
 * carries the request and wraps the answer in the same FeatureCollection shape
 * `parseGPX` produces, so a computed route enters the map exactly like a loaded GPX.
 */

export type RouteMode = 'fast' | 'scenic' | 'curvy';
export type GravelMode = 'exclude' | 'neutral' | 'include';

export interface LatLon {
  lat: number;
  lon: number;
}

export interface GeocodeResult {
  name: string;
  lat: number;
  lon: number;
}

/** "Monschau, Städteregion Aachen, Germany" → "Monschau" — for waypoint labels and default names. */
export function shortPlaceName(name: string): string {
  return name.split(',')[0].trim() || name;
}

export interface RoutePlanRequest {
  from: LatLon;
  /** A→B destination; omit for a round trip. */
  to?: LatLon;
  mode: RouteMode;
  gravel: GravelMode;
  /** Round trip from `from`; omit when `to` is set. */
  roundTrip?: {
    distanceKm: number;
    /** Departure-direction hint, compass degrees. */
    headingDegrees?: number;
    /** Same seed → same loop; new seed → "another loop". */
    seed?: number;
  };
}

export interface PlannedRoute {
  geojson: FeatureCollection;
  distanceMeters: number;
  durationMillis: number;
}

async function throwResponseError(res: Response, fallback: string): Promise<never> {
  // Backend errors are ProblemDetails ({ title, detail }).
  const problem = (await res.json().catch(() => null)) as { title?: string; detail?: string } | null;
  throw new Error(problem?.detail ?? problem?.title ?? `${fallback} (${res.status})`);
}

/**
 * Search-as-you-type place lookup. `bias` (the rider's position) ranks nearby places
 * first; `signal` lets the caller abort a stale request when the query changes.
 */
export async function geocode(
  query: string,
  bias?: LatLon | null,
  signal?: AbortSignal,
): Promise<GeocodeResult[]> {
  let url = `${API_BASE_URL}/geocode?q=${encodeURIComponent(query)}`;
  if (bias) url += `&lat=${bias.lat}&lon=${bias.lon}`;
  const res = await motogoFetch(url, { signal });
  if (!res.ok) await throwResponseError(res, 'Search failed');
  return (await res.json()) as GeocodeResult[];
}

/** `signal` lets the route editor abort a per-segment request superseded by a newer edit. */
export async function fetchRoute(request: RoutePlanRequest, signal?: AbortSignal): Promise<PlannedRoute> {
  const res = await motogoFetch(`${API_BASE_URL}/route`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
    signal,
  });
  if (!res.ok) await throwResponseError(res, 'Routing failed');

  const data = (await res.json()) as {
    coordinates: [number, number][];
    distanceMeters: number;
    durationMillis: number;
  };

  const features: Feature[] = [
    {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: data.coordinates },
      properties: {},
    },
  ];
  // Destination pin for A→B — rendered by the existing Point-filtered route-points
  // layer. Round trips end where they start, so no pin.
  if (request.to && data.coordinates.length > 0) {
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: data.coordinates[data.coordinates.length - 1] },
      properties: {},
    });
  }

  return {
    geojson: { type: 'FeatureCollection', features },
    distanceMeters: data.distanceMeters,
    durationMillis: data.durationMillis,
  };
}

/** Initial great-circle bearing from → to, compass degrees [0, 360). */
export function bearingDegrees(from: LatLon, to: LatLon): number {
  const toRad = Math.PI / 180;
  const phi1 = from.lat * toRad;
  const phi2 = to.lat * toRad;
  const dLambda = (to.lon - from.lon) * toRad;
  const y = Math.sin(dLambda) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLambda);
  return (Math.atan2(y, x) / toRad + 360) % 360;
}
